import { groupBy } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Accordion } from '~/eds/src/components/Accordion/Accordion';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { Text } from '~/eds/src/components/Text/Text';
import { SearchFilter } from '~/types/DashboardV2';

import { FilterItem } from './FilterItem';

type Props = {
  pinnedFiltersIds?: (string | number)[];
  searchFilters: SearchFilter[];
};

const ACCORDION_WIDTH = '260px';

export const FiltersAccordion = ({
  pinnedFiltersIds = [],
  searchFilters,
}: Props) => {
  const [search, setSearch] = useState('');
  const intl = useIntl();

  const sections = useMemo(() => {
    const filtered = searchFilters.filter((filter) =>
      filter.label.toLowerCase().includes(search.toLowerCase()),
    );
    return groupBy(filtered, 'section');
  }, [search, searchFilters]);

  const items = useMemo(() => {
    const pinnedFilters = searchFilters.filter(
      (filter) =>
        pinnedFiltersIds.includes(filter.id) &&
        filter.label.toLowerCase().includes(search.toLowerCase()),
    );

    return [
      ...(pinnedFilters.length > 0
        ? [
            renderSection(
              intl.formatMessage({
                id: 'FiltersAccordion.pinnedFiltersSection',
                defaultMessage: 'Pinned Filters',
                description: 'Section title for pinned filters',
              }),
              pinnedFilters,
              search,
            ),
          ]
        : []),
      ...Object.entries(sections).map(([section, filters]) =>
        renderSection(section, filters, search),
      ),
    ];
  }, [sections, search, pinnedFiltersIds, searchFilters]);

  return (
    <Layout direction="column" spacing={4} h="100%">
      <Layout direction="column" px={4} pt={4} spacing={4}>
        <Text variant="body-bold">
          {intl.formatMessage({
            id: 'FiltersAccordion.filterLabel',
            defaultMessage: 'Filter',
            description: 'Label for filter section',
          })}
        </Text>
        <SearchInput
          name="search"
          placeholder={intl.formatMessage({
            id: 'FiltersAccordion.searchPlaceholder',
            defaultMessage: 'Search by…',
            description: 'Placeholder text for filter search input',
          })}
          value={search}
          onChange={(value) => setSearch(value as string)}
        />
      </Layout>

      <Layout h="100%" overflowY="scroll" px={4}>
        <Accordion
          border="none"
          disableContentPadding={true}
          itemGap="s"
          items={items}
          minW={ACCORDION_WIDTH}
          mode="compact"
          togglePosition="right"
          w={ACCORDION_WIDTH}
        />
      </Layout>
    </Layout>
  );
};

const renderFilter = (
  { id, label }: SearchFilter,
  index: number,
  search: string,
) => (
  <FilterItem key={`${id}_${index}`} id={id} label={label} search={search} />
);

const renderSection = (
  section: string,
  filters: SearchFilter[],
  search: string,
) => ({
  title: section,
  isExpanded: true,
  content: (
    <Layout direction="column">
      {filters.map((filter, index) => renderFilter(filter, index, search))}
    </Layout>
  ),
});
